import React, { useMemo, useState } from 'react';

export type WindDataSource = 'auto' | 'manual' | 'cached';
export type WindFetchStatus = 'idle' | 'loading' | 'ok' | 'error';

interface WindOverlayProps {
  directionDeg?: number;
  speedKmh?: number;
  defaultOpen?: boolean;
  position?: 'top-right' | 'bottom-right' | 'bottom-left';
  editable?: boolean;
  dataSource?: WindDataSource;
  updatedAt?: string | null;
  status?: WindFetchStatus;
  sourceLabel?: string;
  stale?: boolean;
  staleAfterMin?: number;
  refreshIntervalMin?: number;
  canRefresh?: boolean;
  onRefresh?: () => void;
  onSourceChange?: (source: WindDataSource) => void;
  onDirectionChange?: (deg: number) => void;
  onSpeedChange?: (speed: number) => void;
  onRefreshIntervalChange?: (minutes: number) => void;
  onStaleAfterChange?: (minutes: number) => void;
  showAnalysis?: boolean;
  onShowAnalysisChange?: (value: boolean) => void;
}

export const getWindLabel = (deg: number): string => {
  const normalized = ((deg % 360) + 360) % 360;
  if (normalized >= 337.5 || normalized < 22.5) return 'N';
  if (normalized < 67.5) return 'NE';
  if (normalized < 112.5) return 'E';
  if (normalized < 157.5) return 'SE';
  if (normalized < 202.5) return 'S';
  if (normalized < 247.5) return 'SW';
  if (normalized < 292.5) return 'W';
  return 'NW';
};

export const getWindDescription = (speedKmh: number): string => {
  if (speedKmh < 5) return 'Debole';
  if (speedKmh < 20) return 'Moderato';
  if (speedKmh < 40) return 'Sostenuto';
  return 'Forte';
};

const formatUpdatedAt = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const refreshIntervals = [0, 5, 10, 15, 30];
const staleThresholds = [15, 30, 45, 60, 90, 120];

const getSourceMeta = (dataSource: WindDataSource) => {
  switch (dataSource) {
    case 'auto':
      return {
        badge: '📡 Meteo live',
        description: 'Dati meteo letti da rete sulla posizione operativa della SITAC.',
      };
    case 'cached':
      return {
        badge: '🧷 Cache',
        description: 'Ultimo dato meteo reale salvato localmente e riusato senza collegamento.',
      };
    case 'manual':
    default:
      return {
        badge: '✍️ Manuale',
        description: 'Valori inseriti dall’operatore. Usare in assenza di copertura o per correzione sul campo.',
      };
  }
};

const getStatusLabel = (status: WindFetchStatus) => {
  switch (status) {
    case 'loading':
      return 'Aggiornamento…';
    case 'ok':
      return 'Collegamento OK';
    case 'error':
      return 'Errore collegamento';
    case 'idle':
    default:
      return 'Pronto';
  }
};

const InfoBox: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div
    style={{
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 10,
      padding: '8px 10px',
    }}
  >
    <div
      style={{
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        color: '#94a3b8',
        marginBottom: 4,
        fontWeight: 700,
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#f8fafc' }}>{value}</div>
  </div>
);

export const WindOverlay: React.FC<WindOverlayProps> = ({
  directionDeg = 0,
  speedKmh = 0,
  defaultOpen = true,
  position = 'top-right',
  editable = false,
  dataSource = 'manual',
  updatedAt,
  status = 'idle',
  sourceLabel,
  stale = false,
  staleAfterMin = 30,
  refreshIntervalMin = 10,
  canRefresh = false,
  onRefresh,
  onSourceChange,
  onDirectionChange,
  onSpeedChange,
  onRefreshIntervalChange,
  onStaleAfterChange,
  showAnalysis = false,
  onShowAnalysisChange,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  const positionStyle = useMemo(() => {
    switch (position) {
      case 'bottom-left':
        return { left: 10, bottom: 10 };
      case 'bottom-right':
        return { right: 10, bottom: 10 };
      case 'top-right':
      default:
        return { right: 10, top: 10 };
    }
  }, [position]);

  const directionLabel = getWindLabel(directionDeg);
  const speedLabel = getWindDescription(speedKmh);
  const propagationDeg = Math.round(((directionDeg + 180) % 360 + 360) % 360);
  const propagationLabel = getWindLabel(propagationDeg);
  const sourceMeta = getSourceMeta(dataSource);
  const manualDisabled = dataSource !== 'manual';

  return (
    <div
      style={{
        position: 'absolute',
        zIndex: 1200,
        ...positionStyle,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 8,
        pointerEvents: 'auto',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          border: '1px solid rgba(255,255,255,0.18)',
          background: '#0f172a',
          color: '#f8fafc',
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: '0.85rem',
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow: '0 8px 20px rgba(0,0,0,0.28)',
        }}
        aria-expanded={open}
      >
        {open ? '✖ Vento' : '🌬️ Vento'}
      </button>

      {open && (
        <div
          style={{
            width: 330,
            maxWidth: 'calc(100vw - 24px)',
            background: 'rgba(15, 23, 42, 0.96)',
            color: '#f8fafc',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 12,
            padding: 14,
            boxShadow: '0 10px 28px rgba(0,0,0,0.35)',
            backdropFilter: 'blur(6px)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
            <div style={{ fontSize: '0.95rem', fontWeight: 800, letterSpacing: 0.2 }}>VENTO OPERATIVO</div>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 8px',
                borderRadius: 999,
                background: stale ? 'rgba(239,68,68,0.18)' : 'rgba(59,130,246,0.16)',
                border: stale ? '1px solid rgba(248,113,113,0.4)' : '1px solid rgba(96,165,250,0.28)',
                color: stale ? '#fecaca' : '#bfdbfe',
                fontSize: '0.72rem',
                fontWeight: 800,
              }}
            >
              {sourceMeta.badge}
            </span>
          </div>

          <div style={{ fontSize: '0.74rem', lineHeight: 1.4, color: '#cbd5e1', marginBottom: 12 }}>{sourceMeta.description}</div>

          {editable && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              {(['auto', 'manual'] as WindDataSource[]).map((mode) => {
                const active = dataSource === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => onSourceChange?.(mode)}
                    style={{
                      flex: 1,
                      minWidth: 110,
                      borderRadius: 8,
                      border: active ? '1px solid #60a5fa' : '1px solid rgba(255,255,255,0.12)',
                      background: active ? 'rgba(37,99,235,0.22)' : 'rgba(255,255,255,0.04)',
                      color: active ? '#dbeafe' : '#e2e8f0',
                      padding: '8px 10px',
                      fontSize: '0.78rem',
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    {mode === 'auto' ? '📡 AUTO' : '✍️ MANUALE'}
                  </button>
                );
              })}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '62px 1fr', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            {/* Bussola operativa: punta rossa = propagazione fuoco (= direzione mappa) */}
            <svg
              width="62" height="62" viewBox="-31 -31 62 62"
              style={{ transform: `rotate(${propagationDeg}deg)`, transition: 'transform 0.4s ease', overflow: 'visible' }}
            >
              {/* Cerchio bussola */}
              <circle cx="0" cy="0" r="29" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.18)" strokeWidth="1.5"/>

              {/* Tacche cardinali fisse (N in alto = 0° = nord mappa) */}
              {[0, 90, 180, 270].map((a) => {
                const rad = (a * Math.PI) / 180;
                const x1 = Math.sin(rad) * 23, y1 = -Math.cos(rad) * 23;
                const x2 = Math.sin(rad) * 28, y2 = -Math.cos(rad) * 28;
                return <line key={a} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(255,255,255,0.30)" strokeWidth="1.5"/>;
              })}

              {/* Coda: provenienza vento (corta, grigia) */}
              <line x1="0" y1="0" x2="0" y2="16" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"/>

              {/* Freccia propagazione fuoco (lunga, rossa, con punta) */}
              <line x1="0" y1="0" x2="0" y2="-20" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round"/>
              <polygon points="0,-27 -4.5,-18 4.5,-18" fill="#ef4444"/>
            </svg>

            <div style={{ display: 'grid', gap: 4 }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 800 }}>
                Vento da: {directionLabel} ({Math.round(directionDeg)}°)
              </div>
              <div style={{ fontSize: '0.78rem', color: '#ef4444', fontWeight: 700 }}>
                ▲ Propagazione → {propagationLabel} ({propagationDeg}°)
              </div>
              <div style={{ fontSize: '0.78rem', color: '#cbd5e1' }}>
                Intensità: {Math.round(speedKmh)} km/h
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            <InfoBox label="Classe" value={speedLabel} />
            <InfoBox label="Settore" value={directionLabel} />
            <InfoBox label="Spinta fronte" value={`${propagationLabel} · ${propagationDeg}°`} />
            <InfoBox label="Stato" value={getStatusLabel(status)} />
          </div>

          <div
            style={{
              display: 'grid',
              gap: 6,
              marginBottom: 12,
              padding: 10,
              borderRadius: 10,
              border: stale ? '1px solid rgba(248,113,113,0.35)' : '1px solid rgba(255,255,255,0.08)',
              background: stale ? 'rgba(239,68,68,0.10)' : 'rgba(255,255,255,0.04)',
              fontSize: '0.76rem',
              color: '#cbd5e1',
            }}
          >
            <div><strong>Fonte:</strong> {sourceLabel ?? sourceMeta.badge}</div>
            <div><strong>Aggiornato:</strong> {formatUpdatedAt(updatedAt)}</div>
            <div><strong>Validità:</strong> {dataSource === 'manual' ? 'Manuale operatore' : stale ? `Dato troppo vecchio (> ${staleAfterMin} min)` : 'Dato entro soglia'}</div>

            {editable && (
              <>
                <div style={{ marginTop: 6 }}>
                  <label style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.76rem', fontWeight: 700, marginBottom: 4 }}>
                    <span>Auto-refresh</span>
                    <span>{refreshIntervalMin <= 0 ? 'OFF' : `${refreshIntervalMin} min`}</span>
                  </label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {refreshIntervals.map((minutes) => (
                      <button
                        key={minutes}
                        type="button"
                        onClick={() => onRefreshIntervalChange?.(minutes)}
                        style={{
                          flex: 1,
                          minWidth: 48,
                          borderRadius: 8,
                          border: refreshIntervalMin === minutes ? '1px solid #60a5fa' : '1px solid rgba(255,255,255,0.12)',
                          background: refreshIntervalMin === minutes ? 'rgba(37,99,235,0.22)' : 'rgba(255,255,255,0.04)',
                          color: '#e2e8f0',
                          padding: '6px 8px',
                          fontSize: '0.74rem',
                          fontWeight: 800,
                          cursor: 'pointer',
                        }}
                      >
                        {minutes <= 0 ? 'OFF' : `${minutes}m`}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.76rem', fontWeight: 700, marginBottom: 4 }}>
                    <span>Soglia dato vecchio</span>
                    <span>{staleAfterMin} min</span>
                  </label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {staleThresholds.map((minutes) => (
                      <button
                        key={minutes}
                        type="button"
                        onClick={() => onStaleAfterChange?.(minutes)}
                        style={{
                          flex: 1,
                          minWidth: 48,
                          borderRadius: 8,
                          border: staleAfterMin === minutes ? '1px solid #f59e0b' : '1px solid rgba(255,255,255,0.12)',
                          background: staleAfterMin === minutes ? 'rgba(245,158,11,0.18)' : 'rgba(255,255,255,0.04)',
                          color: '#e2e8f0',
                          padding: '6px 8px',
                          fontSize: '0.74rem',
                          fontWeight: 800,
                          cursor: 'pointer',
                        }}
                      >
                        {minutes}m
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {canRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={status === 'loading'}
                style={{
                  marginTop: 8,
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.12)',
                  background: status === 'loading' ? 'rgba(255,255,255,0.08)' : 'rgba(37,99,235,0.22)',
                  color: '#e0f2fe',
                  padding: '8px 10px',
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  cursor: status === 'loading' ? 'wait' : 'pointer',
                }}
              >
                {status === 'loading' ? '⏳ Aggiorno…' : '🔄 Aggiorna da rete'}
              </button>
            )}
          </div>

          {editable && (
            <div
              style={{
                display: 'grid',
                gap: 10,
                marginBottom: 12,
                padding: 10,
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.08)',
                background: manualDisabled ? 'rgba(148,163,184,0.08)' : 'rgba(255,255,255,0.04)',
                opacity: manualDisabled ? 0.7 : 1,
              }}
            >
              <div>
                <label style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', fontWeight: 700, marginBottom: 4 }}>
                  <span>Direzione manuale</span>
                  <span>{Math.round(directionDeg)}°</span>
                </label>
                <input type="range" min="0" max="360" step="1" value={directionDeg} disabled={manualDisabled} onChange={(e) => onDirectionChange?.(parseInt(e.target.value, 10))} style={{ width: '100%' }} />
              </div>

              <div>
                <label style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', fontWeight: 700, marginBottom: 4 }}>
                  <span>Velocità manuale</span>
                  <span>{Math.round(speedKmh)} km/h</span>
                </label>
                <input type="range" min="0" max="80" step="1" value={speedKmh} disabled={manualDisabled} onChange={(e) => onSpeedChange?.(parseInt(e.target.value, 10))} style={{ width: '100%' }} />
              </div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[0, 10, 20, 40, 60].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    disabled={manualDisabled}
                    onClick={() => onSpeedChange?.(preset)}
                    style={{
                      flex: 1,
                      minWidth: 52,
                      borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.12)',
                      background: 'rgba(255,255,255,0.04)',
                      color: '#e2e8f0',
                      padding: '6px 8px',
                      fontSize: '0.76rem',
                      fontWeight: 800,
                      cursor: manualDisabled ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {preset}
                  </button>
                ))}
              </div>
            </div>
          )}



          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              marginTop: 10,
              paddingTop: 10,
              borderTop: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#f8fafc' }}>
              Mostra analisi propagazione
            </div>

            <input
              type="checkbox"
              checked={showAnalysis}
              onChange={(e) => onShowAnalysisChange?.(e.target.checked)}
              style={{ width: 18, height: 18, cursor: 'pointer' }}
            />
          </div>
          <div style={{ fontSize: '0.74rem', lineHeight: 1.4, color: '#cbd5e1', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 10 }}>
            Supporto decisionale SITAC: il cono di propagazione usa il vento disponibile e il perimetro rilevato. Non è una previsione fisica completa del fuoco.
          </div>
        </div>
      )}
    </div>
  );
};

export default WindOverlay;
